import { useMutation } from '@apollo/client';

import useLogEvent, { LogLevel } from '../../../helper/useLogEvent';
import { ModifyOneResult } from '../../../models/MutationResultModels';
import BookingSetting, { BookingSettingInsertInput } from '../models/BookingSetting';
import { UPDATE_BOOKING_SETTINGS } from './query';
import useFetchBookingSettings from './useFetchBookingSettings';

interface MutationData {
  result: ModifyOneResult<BookingSetting>;
}

interface MutationVariables {
  query: {
    _id: string;
  };
  data: BookingSettingInsertInput;
}

/**
 * Update a booking's settings
 *
 * @param {string} bookingId ID of the current booking (NB: we cannot relly on BoutiqueContext here, because it can be undefined if this function is called directly from the BoutiqueContext component)
 */
function useSaveBookingSettings(bookingId: string) {
  const { logEvent } = useLogEvent();

  const { fetchBookingSettings } = useFetchBookingSettings();

  const [execute, { data, loading, error, client }] = useMutation<MutationData, MutationVariables>(
    UPDATE_BOOKING_SETTINGS,
  );

  /**
   * Replace all settings
   *
   * @param settings
   */
  const saveSettings = async (settings: BookingSetting) => {
    const currentSettings: BookingSettingInsertInput = {
      ...settings,
      bookingId,
    };

    const response = await execute({
      variables: {
        query: {
          _id: currentSettings._id,
        },
        data: currentSettings,
      },
    });

    await client.refetchQueries({
      updateCache(cache) {
        cache.evict({ fieldName: 'bookingSetting' });
        cache.evict({ fieldName: 'bookingTemplates' });
      },
      optimistic: true,
    });

    logEvent(LogLevel.Info, 'Save booking settings', {
      id: response.data?.result.value?._id,
    });

    return response.data?.result.value?._id;
  };

  /**
   * Replace a specific settings
   *
   * @param settingsToUpdate
   */
  const saveSetting = async (settingsToUpdate: Partial<BookingSetting>) => {
    const settings = await fetchBookingSettings(bookingId);
    if (!settings) {
      return;
    }

    const newSettings: BookingSetting = {
      ...settings,
      ...settingsToUpdate,
    };

    await saveSettings(newSettings);

    return newSettings;
  };

  return { saveSettings, saveSetting, loading, error, id: data?.result.value?._id };
}

export default useSaveBookingSettings;
